(function () {
  'use strict';

  var configuratorRoot = document.querySelector('[data-custom-chopstick-configurator-root]');
  var browser = configuratorRoot && configuratorRoot.querySelector('[data-configurator-sample-browser]');
  var viewport = browser && browser.querySelector('[data-configurator-sample-browser-viewport]');
  if (
    !configuratorRoot ||
    !browser ||
    !viewport ||
    configuratorRoot.configuratorSampleBrowser ||
    !window.THREE ||
    !window.ERSChopstickSleeveGeometry
  ) return;

  var THREE = window.THREE;
  var SOURCE_WIDTH = 841.89;
  var SOURCE_HEIGHT = 595.275;
  var ARTWORK_WIDTH = 1680;
  var ARTWORK_HEIGHT = 160;
  var FACE_CROP = { x: 83.25, width: 675, height: 63 };
  var FACE_Y = { front: 243, back: 318 };
  var DEFAULT_ROTATION = -.22;
  var ROTATION_MIN = -.42;
  var ROTATION_MAX = Math.PI + .42;
  var TILT_LIMIT = .16;
  var EASING = .18;
  var sourceUrls = {};
  var records = [];
  var imagePromises = {};
  var intersectionObserver = null;
  var resizeObserver = null;
  var scrollFallbackBound = false;
  var motionQuery = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var active = false;
  var destroyed = false;
  var activeRenderers = 0;
  var rendererCreations = 0;
  var pendingFrames = 0;
  var renderedFrames = 0;

  Array.prototype.forEach.call(browser.querySelectorAll('[data-configurator-sample-source]'), function (source) {
    sourceUrls[source.getAttribute('data-configurator-sample-source')] = source.getAttribute('data-configurator-sample-source-url');
  });

  function clamp(value, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, value));
  }

  function createArtworkCanvas() {
    var canvas = document.createElement('canvas');
    canvas.width = ARTWORK_WIDTH;
    canvas.height = ARTWORK_HEIGHT;
    return canvas;
  }

  function loadSource(sourceUrl) {
    if (imagePromises[sourceUrl]) return imagePromises[sourceUrl];
    imagePromises[sourceUrl] = new Promise(function (resolve, reject) {
      var image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = function () { resolve(image); };
      image.onerror = function () { reject(new Error('Sample artwork could not be loaded.')); };
      image.src = sourceUrl;
    });
    return imagePromises[sourceUrl];
  }

  function renderFace(source, side) {
    var canvas = createArtworkCanvas();
    var context = canvas.getContext('2d');
    var sourceWidth = source.naturalWidth || source.width;
    var sourceHeight = source.naturalHeight || source.height;
    var scaleX = sourceWidth / SOURCE_WIDTH;
    var scaleY = sourceHeight / SOURCE_HEIGHT;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(
      source,
      FACE_CROP.x * scaleX,
      FACE_Y[side] * scaleY,
      FACE_CROP.width * scaleX,
      FACE_CROP.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // The source sheets include print-guide dashes along the outer edge of each
    // face. They are editor reference marks, not part of the physical sample.
    context.fillStyle = '#ffffff';
    if (side === 'front') context.fillRect(0, 0, canvas.width, 5);
    else context.fillRect(0, canvas.height - 5, canvas.width, 5);
    return canvas;
  }

  function loadArtwork(sampleId) {
    var sourceUrl = sourceUrls[sampleId];
    if (!sourceUrl) return Promise.reject(new Error('Sample source is unavailable.'));
    return loadSource(sourceUrl).then(function (source) {
      return {
        front: renderFace(source, 'front'),
        back: renderFace(source, 'back')
      };
    });
  }

  function frameCamera(record) {
    var width = record.host.clientWidth;
    var height = record.host.clientHeight;
    if (!width || !height) return false;
    var aspect = width / height;
    var verticalTangent = Math.tan(THREE.MathUtils.degToRad(record.camera.fov * .5));
    var horizontalTangent = verticalTangent * aspect;
    var frameFill = width < 520 ? .9 : .92;
    var rotationCosine = Math.cos(record.sleeve.rotation.y);
    var rotationSine = Math.sin(record.sleeve.rotation.y);
    var fittedDistance = 0;

    [-3.85, 3.85].forEach(function (x) {
      [-.06, .06].forEach(function (z) {
        var rotatedX = x * rotationCosine + z * rotationSine;
        var rotatedZ = -x * rotationSine + z * rotationCosine;
        fittedDistance = Math.max(
          fittedDistance,
          rotatedZ + Math.max(
            Math.abs(rotatedX) / (horizontalTangent * frameFill),
            .4 / (verticalTangent * frameFill)
          )
        );
      });
    });

    record.camera.aspect = aspect;
    record.camera.position.set(0, .12, fittedDistance + .08);
    record.camera.lookAt(0, 0, 0);
    record.camera.updateProjectionMatrix();
    return true;
  }

  function renderRecord(record) {
    if (!record.renderer || !record.scene || !record.camera) return;
    record.renderer.render(record.scene, record.camera);
    renderedFrames += 1;
  }

  function resizeRecord(record) {
    if (!record.renderer || !record.host.clientWidth || !record.host.clientHeight) return false;
    var pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
    var width = Math.round(record.host.clientWidth);
    var height = Math.round(record.host.clientHeight);
    if (width === record.renderedWidth && height === record.renderedHeight && pixelRatio === record.pixelRatio) return true;
    record.renderedWidth = width;
    record.renderedHeight = height;
    record.pixelRatio = pixelRatio;
    record.renderer.setPixelRatio(pixelRatio);
    record.renderer.setSize(width, height, false);
    frameCamera(record);
    renderRecord(record);
    return true;
  }

  function cancelPose(record) {
    if (!record.frame) return;
    window.cancelAnimationFrame(record.frame);
    record.frame = null;
    pendingFrames = Math.max(0, pendingFrames - 1);
  }

  function animatePose(record) {
    record.frame = null;
    pendingFrames = Math.max(0, pendingFrames - 1);
    if (!record.renderer || destroyed) return;
    var rotationDifference = record.targetRotation - record.sleeve.rotation.y;
    var tiltDifference = record.targetTilt - record.sleeve.rotation.x;
    record.sleeve.rotation.y += rotationDifference * EASING;
    record.sleeve.rotation.x += tiltDifference * EASING;
    frameCamera(record);
    renderRecord(record);
    if (Math.abs(rotationDifference) > .001 || Math.abs(tiltDifference) > .001) schedulePose(record);
  }

  function schedulePose(record) {
    if (!record.renderer || record.frame) return;
    if (motionQuery && motionQuery.matches) {
      record.sleeve.rotation.y = record.targetRotation;
      record.sleeve.rotation.x = record.targetTilt;
      frameCamera(record);
      renderRecord(record);
      return;
    }
    pendingFrames += 1;
    record.frame = window.requestAnimationFrame(function () { animatePose(record); });
  }

  function removePointerEvents(record) {
    if (!record.canvas || !record.pointerHandlers) return;
    record.canvas.removeEventListener('pointerdown', record.pointerHandlers.down);
    record.canvas.removeEventListener('pointermove', record.pointerHandlers.move);
    record.canvas.removeEventListener('pointerup', record.pointerHandlers.up);
    record.canvas.removeEventListener('pointercancel', record.pointerHandlers.up);
    record.canvas.removeEventListener('lostpointercapture', record.pointerHandlers.up);
    record.canvas.removeEventListener('click', record.pointerHandlers.click);
    record.pointerHandlers = null;
  }

  function addPointerEvents(record) {
    var canvas = record.canvas;
    var startX = 0;
    var startY = 0;
    var lastX = 0;
    var lastY = 0;
    var pointerId = null;
    var dragging = false;
    var moved = false;

    function pointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      pointerId = event.pointerId;
      dragging = true;
      moved = false;
      startX = lastX = event.clientX;
      startY = lastY = event.clientY;
      canvas.classList.add('is-dragging');
      if (event.isTrusted && canvas.setPointerCapture) canvas.setPointerCapture(pointerId);
    }

    function pointerMove(event) {
      if (!dragging || event.pointerId !== pointerId) return;
      var deltaX = event.clientX - lastX;
      var deltaY = event.clientY - lastY;
      if (Math.abs(event.clientX - startX) + Math.abs(event.clientY - startY) > 5) moved = true;
      record.targetRotation = clamp(record.targetRotation + deltaX * .012, ROTATION_MIN, ROTATION_MAX);
      record.targetTilt = clamp(record.targetTilt + deltaY * .004, -TILT_LIMIT, TILT_LIMIT);
      lastX = event.clientX;
      lastY = event.clientY;
      schedulePose(record);
    }

    function pointerUp(event) {
      if (!dragging || (event.pointerId !== undefined && event.pointerId !== pointerId)) return;
      dragging = false;
      pointerId = null;
      canvas.classList.remove('is-dragging');
    }

    function click(event) {
      if (!moved) return;
      moved = false;
      event.preventDefault();
      event.stopPropagation();
    }

    record.pointerHandlers = { down: pointerDown, move: pointerMove, up: pointerUp, click: click };
    canvas.addEventListener('pointerdown', pointerDown);
    canvas.addEventListener('pointermove', pointerMove);
    canvas.addEventListener('pointerup', pointerUp);
    canvas.addEventListener('pointercancel', pointerUp);
    canvas.addEventListener('lostpointercapture', pointerUp);
    canvas.addEventListener('click', click);
  }

  function disposeScene(record) {
    var geometries = [];
    var materials = [];
    if (!record.scene) return;
    record.scene.traverse(function (object) {
      if (object.geometry && geometries.indexOf(object.geometry) === -1) geometries.push(object.geometry);
      if (!object.material) return;
      (Array.isArray(object.material) ? object.material : [object.material]).forEach(function (material) {
        if (materials.indexOf(material) === -1) materials.push(material);
      });
    });
    geometries.forEach(function (geometry) { geometry.dispose(); });
    materials.forEach(function (material) { material.dispose(); });
    if (record.frontTexture) record.frontTexture.dispose();
    if (record.backTexture) record.backTexture.dispose();
  }

  function deactivateRecord(record) {
    if (!record.renderer) return;
    cancelPose(record);
    removePointerEvents(record);
    disposeScene(record);
    record.renderer.dispose();
    if (typeof record.renderer.forceContextLoss === 'function') record.renderer.forceContextLoss();
    if (record.canvas && record.canvas.parentNode === record.host) record.host.removeChild(record.canvas);
    record.renderer = null;
    record.scene = null;
    record.camera = null;
    record.sleeve = null;
    record.frontTexture = null;
    record.backTexture = null;
    record.canvas = null;
    record.renderedWidth = 0;
    record.renderedHeight = 0;
    record.pixelRatio = 0;
    activeRenderers = Math.max(0, activeRenderers - 1);
    record.host.setAttribute('data-sample-preview-renderer', 'inactive');
  }

  function activateRecord(record) {
    if (!active || !record.visible || !record.artwork || record.renderer || destroyed) return;
    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
    } catch (error) {
      record.host.setAttribute('data-sample-preview-status', 'unavailable');
      record.host.querySelector('[data-configurator-sample-preview-status]').textContent = '3D preview unavailable';
      return;
    }

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(23, 1, .1, 100);
    var frontTexture = new THREE.CanvasTexture(record.artwork.front);
    var backTexture = new THREE.CanvasTexture(record.artwork.back);
    frontTexture.colorSpace = THREE.SRGBColorSpace;
    backTexture.colorSpace = THREE.SRGBColorSpace;
    frontTexture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
    backTexture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
    var sleeve = window.ERSChopstickSleeveGeometry.create(THREE, frontTexture, backTexture);
    sleeve.rotation.set(-.045, record.targetRotation, 0);
    sleeve.traverse(function (object) {
      if (!object.isMesh) return;
      object.castShadow = false;
      object.receiveShadow = false;
    });
    scene.add(sleeve);
    scene.add(new THREE.HemisphereLight(0xfffdf7, 0xa9aaa5, 1.75));
    var key = new THREE.DirectionalLight(0xfffbf4, 2.1);
    key.position.set(-3.5, 4.5, 5);
    scene.add(key);
    var fill = new THREE.DirectionalLight(0xe4eceb, .68);
    fill.position.set(4, 1, 3);
    scene.add(fill);

    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(0x000000, 0);
    record.renderer = renderer;
    record.scene = scene;
    record.camera = camera;
    record.sleeve = sleeve;
    record.frontTexture = frontTexture;
    record.backTexture = backTexture;
    record.canvas = renderer.domElement;
    record.canvas.setAttribute('aria-hidden', 'true');
    record.host.appendChild(record.canvas);
    addPointerEvents(record);
    activeRenderers += 1;
    rendererCreations += 1;
    record.host.setAttribute('data-sample-preview-renderer', 'active');
    record.host.setAttribute('data-sample-preview-status', 'ready');
    record.host.querySelector('[data-configurator-sample-preview-status]').textContent = '';
    resizeRecord(record);
  }

  function refreshVisibility() {
    if (!active || destroyed) return;
    var viewportRect = viewport.getBoundingClientRect();
    records.forEach(function (record) {
      var rect = record.host.getBoundingClientRect();
      record.visible = rect.bottom >= viewportRect.top - 180 && rect.top <= viewportRect.bottom + 180;
      if (record.visible) activateRecord(record);
      else deactivateRecord(record);
    });
  }

  function mount(host, sampleId) {
    if (!host || !sourceUrls[sampleId] || destroyed) return Promise.reject(new Error('Unknown sample.'));
    var status = document.createElement('span');
    status.className = 'custom-chopstick-sample-preview__status';
    status.setAttribute('data-configurator-sample-preview-status', '');
    status.textContent = 'Preparing physical sample…';
    host.textContent = '';
    host.appendChild(status);
    host.setAttribute('data-sample-preview-status', 'loading');
    host.setAttribute('data-sample-preview-renderer', 'inactive');
    host.setAttribute('data-configurator-sample-preview-id', sampleId);

    var record = {
      id: sampleId,
      host: host,
      artwork: null,
      visible: false,
      renderer: null,
      scene: null,
      camera: null,
      sleeve: null,
      frontTexture: null,
      backTexture: null,
      canvas: null,
      targetRotation: DEFAULT_ROTATION,
      targetTilt: -.045,
      frame: null,
      pointerHandlers: null,
      renderedWidth: 0,
      renderedHeight: 0,
      pixelRatio: 0
    };
    records.push(record);
    if (intersectionObserver) intersectionObserver.observe(host);
    if (resizeObserver) resizeObserver.observe(host);

    return loadArtwork(sampleId).then(function (artwork) {
      if (destroyed || records.indexOf(record) === -1) return;
      record.artwork = artwork;
      record.host.setAttribute('data-sample-preview-status', 'artwork-ready');
      refreshVisibility();
    }).catch(function () {
      if (destroyed || records.indexOf(record) === -1) return;
      record.host.setAttribute('data-sample-preview-status', 'unavailable');
      status.textContent = 'Sample preview unavailable';
    });
  }

  function clear() {
    records.slice().forEach(function (record) {
      if (intersectionObserver) intersectionObserver.unobserve(record.host);
      if (resizeObserver) resizeObserver.unobserve(record.host);
      deactivateRecord(record);
    });
    records.length = 0;
  }

  function setActive(nextActive) {
    active = Boolean(nextActive);
    browser.setAttribute('aria-hidden', active ? 'false' : 'true');
    if (active) browser.removeAttribute('inert');
    else browser.setAttribute('inert', '');
    if (!active) {
      records.forEach(deactivateRecord);
      return;
    }
    window.requestAnimationFrame(refreshVisibility);
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    clear();
    if (intersectionObserver) intersectionObserver.disconnect();
    if (resizeObserver) resizeObserver.disconnect();
    if (scrollFallbackBound) viewport.removeEventListener('scroll', refreshVisibility);
    window.removeEventListener('pagehide', handlePageHide);
    configuratorRoot.configuratorSampleBrowser = null;
  }

  function handlePageHide(event) {
    if (!event.persisted) destroy();
  }

  if ('IntersectionObserver' in window) {
    intersectionObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var record = records.find(function (item) { return item.host === entry.target; });
        if (!record) return;
        record.visible = entry.isIntersecting;
        if (active && record.visible) activateRecord(record);
        else deactivateRecord(record);
      });
    }, { root: viewport, rootMargin: '180px 0px', threshold: 0 });
  } else {
    scrollFallbackBound = true;
    viewport.addEventListener('scroll', refreshVisibility, { passive: true });
  }

  if ('ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(function (entries) {
      entries.forEach(function (entry) {
        var record = records.find(function (item) { return item.host === entry.target; });
        if (record && record.renderer) resizeRecord(record);
      });
    });
  }

  window.addEventListener('pagehide', handlePageHide);

  configuratorRoot.configuratorSampleBrowser = {
    mount: mount,
    clear: clear,
    setActive: setActive,
    getMetrics: function () {
      return {
        mounted: records.length,
        activeRenderers: activeRenderers,
        rendererCreations: rendererCreations,
        pendingFrames: pendingFrames,
        renderedFrames: renderedFrames
      };
    },
    destroy: destroy
  };
}());
